import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { api, type InitBetBody } from '../lib/api';

// Onboarding — first-time-user wizard that captures the bet parameters and
// posts them to /bet/init. Auto-shown by the app's RequireBet wrapper
// whenever an authenticated user has no bet row yet.
//
// One screen, every field on display (no multi-step gymnastics — fewer
// surfaces means fewer ways to abandon mid-flow). Sensible defaults pre-
// fill from Gio's canonical bet so a returning Gio just clicks "commit"
// while a new user can edit each line.
export default function Onboarding() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const nextMonday = nextMondayISO(today);
  const oneYearOut = isoNDaysAfter(today, 365);

  const [startWeight, setStartWeight] = useState('96');
  const [targetWeight, setTargetWeight] = useState('72');
  const [startDate, setStartDate] = useState(nextMonday);
  const [deadline, setDeadline] = useState(oneYearOut);
  const [height, setHeight] = useState('175');
  const [stakes, setStakes] = useState('150000');
  const [bufferWeight, setBufferWeight] = useState('70');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the user already committed, bounce them straight to /dashboard.
  // Avoids confusion if they hit /onboarding manually later.
  useEffect(() => {
    void api
      .getBet()
      .then(() => navigate('/dashboard', { replace: true }))
      .catch(() => undefined);
  }, [navigate]);

  const submit = async () => {
    setError(null);
    // Reject empty strings up front. Number('') === 0, which would otherwise
    // cascade into a misleading "target must be less than start" error when
    // really the user just hasn't typed a starting weight yet.
    if (startWeight.trim() === '') {
      setError('Enter your starting weight');
      return;
    }
    if (targetWeight.trim() === '') {
      setError('Enter your target weight');
      return;
    }
    if (height.trim() === '') {
      setError('Enter your height');
      return;
    }
    if (startDate === '' || deadline === '') {
      setError('Pick both a start date and a deadline');
      return;
    }
    const startKg = Number(startWeight);
    const targetKg = Number(targetWeight);
    const heightCm = Number(height);
    const stakesUsd = stakes.trim() === '' ? 0 : Number(stakes);
    const bufferKg = bufferWeight.trim() === '' ? 0 : Number(bufferWeight);
    if (!Number.isFinite(startKg) || startKg < 30 || startKg > 300) {
      setError('Starting weight must be 30–300 kg');
      return;
    }
    if (!Number.isFinite(targetKg) || targetKg >= startKg) {
      setError('Target weight must be less than starting weight');
      return;
    }
    if (!Number.isFinite(heightCm) || heightCm < 100 || heightCm > 250) {
      setError('Height must be 100–250 cm');
      return;
    }
    if (!Number.isFinite(stakesUsd) || stakesUsd < 0) {
      setError('Stakes must be a non-negative number');
      return;
    }
    if (deadline <= startDate) {
      setError('Deadline must be after the start date');
      return;
    }
    // Buffer is optional. If supplied, sanity-check bounds matching the
    // server. If invalid we surface a specific message instead of letting
    // the server's 400 land as an opaque error.
    if (bufferKg > 0 && bufferKg >= targetKg) {
      setError('Buffer target must be lower than target weight');
      return;
    }
    const body: InitBetBody = {
      starting_weight_kg: startKg,
      target_weight_kg: targetKg,
      starting_date: startDate,
      deadline_date: deadline,
      height_cm: heightCm,
      stakes_usd: stakesUsd,
    };
    if (Number.isFinite(bufferKg) && bufferKg > 0 && bufferKg < targetKg) {
      body.buffer_target_kg = bufferKg;
    }
    setBusy(true);
    try {
      await api.initBet(body);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-full max-w-md mx-auto px-5 pt-10 pb-32 space-y-5">
      <header className="space-y-2">
        <p className="label-eyebrow">GIOS · ONBOARDING</p>
        <h1 className="font-display text-5xl leading-none">
          Set your bet
        </h1>
        <p className="text-sm text-fg-muted leading-relaxed">
          Every field can be edited later from settings, but the start &amp;
          deadline anchor your weekly schedule — choose them carefully.
        </p>
      </header>

      <Card eyebrow="Weight">
        <Field label="Starting weight (kg)" value={startWeight} onChange={setStartWeight} type="number" step="0.1" />
        <Field label="Target weight (kg)" value={targetWeight} onChange={setTargetWeight} type="number" step="0.1" />
        <Field
          label="Buffer target (kg, optional)"
          hint="A more aggressive milestone — hit this before deadline for a safety cushion. Must be below target."
          value={bufferWeight}
          onChange={setBufferWeight}
          type="number"
          step="0.1"
        />
      </Card>

      <Card eyebrow="Calendar">
        <Field
          label="Start date"
          hint="Pick a Monday — week 1 begins this day."
          value={startDate}
          onChange={setStartDate}
          type="date"
        />
        <Field label="Deadline" value={deadline} onChange={setDeadline} type="date" />
      </Card>

      <Card eyebrow="You">
        <Field label="Height (cm)" value={height} onChange={setHeight} type="number" />
        <Field label="Stakes (USD, for display)" value={stakes} onChange={setStakes} type="number" />
      </Card>

      {error && (
        <Chip tone="danger" dot>
          {error}
        </Chip>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={busy}
        className="btn-primary w-full py-3.5 font-mono uppercase tracking-[0.16em] text-sm"
      >
        {busy ? 'Committing…' : 'Commit to the bet →'}
      </button>

      <nav className="flex items-center justify-center gap-3 pt-1">
        <Link
          to="/login"
          className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg"
        >
          Sign out
        </Link>
      </nav>
    </div>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  step,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (s: string) => void;
  type?: string;
  step?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="label-eyebrow">{label}</span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 font-mono tabular-nums text-sm text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
      />
      {hint && <p className="text-xs text-fg-muted leading-snug pt-0.5">{hint}</p>}
    </label>
  );
}

function nextMondayISO(fromISO: string): string {
  const d = new Date(`${fromISO}T00:00:00`);
  const wd = d.getDay(); // Sun=0..Sat=6
  const diff = wd === 1 ? 0 : (8 - wd) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function isoNDaysAfter(fromISO: string, n: number): string {
  const d = new Date(`${fromISO}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
